import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import roomContext from "../context/rooms/roomContext";
import ConfirmBooking from "./ConfirmBooking";
import "../styles/RoomBooking.css";

const Roombooking = () => {
  const bookingContext = useContext(roomContext);
  const {
    numberOfRooms,
    setNumberOfRooms,
    numberOfGuest,
    setNumberOfGuest,
    name,
    setName,
    age,
    setAge,
    aadhar,
    setAadhar,
    mobileNumber,
    setMobileNumber,
    email,
    setEmail,
    address,
    setAddress,
    checkInDate,
    setCheckInDate,
    checkOutDate,
    setCheckOutDate,
    availableRooms,
    setAvailableRooms,
  } = bookingContext;

  const location = useLocation();
  const { room } = location.state || {};
  const { type, beds, amount, totalRooms } = room || {};

  const [confirm, setConfirm] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true); // Store available rooms
  const [errors, setErrors] = useState({});
  const [inProgress, setInProgress] = useState(false);
  const [dateError, setdateError] = useState("");

  const fieldRefs = useRef({});

  const resetForm = () => {
    setNumberOfRooms(0);
    setNumberOfGuest(0);
    setName([]);
    setAge([]);
    setAadhar([]);
    setMobileNumber("");
    setEmail("");
    setAddress({ country: "", state: "", district: "", city: "", pincode: "" });
    setCheckInDate("");
    setCheckOutDate("");
    setAvailableRooms(0);
  };

  // Reset form when user navigates away
  useEffect(() => {
    return () => {
      resetForm();
    };
    // eslint-disable-next-line
  }, []);

  const today = new Date();
  const year = today.getFullYear();

  // Set minimum selectable date (May 25 or today, whichever is later)
  const minDate = new Date(year, 4, 25); // May 25
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const minFormatted =
    tomorrow > minDate
      ? tomorrow.toISOString().split("T")[0]
      : minDate.toISOString().split("T")[0];

  // Set maximum selectable date (October 10)
  const maxDate = new Date(year, 9, 12); // October 10
  const maxFormatted = maxDate.toISOString().split("T")[0];

  let newErrors = {};

  const handleCheckInChange = (e) => {
    let selectedDateStr = e.target.value;
    let selectedDate = new Date(selectedDateStr); // Convert to Date


    const minDateObj = new Date(minFormatted);
    const maxDateObj = new Date(maxFormatted);

    if (selectedDate < minDateObj) {
      selectedDate = minDateObj;
      newErrors.checkInDate = `Check-in must be from ${minFormatted} onwards.`;
      setErrors(newErrors);
    } else if (selectedDate > maxDateObj) {
      selectedDate = maxDateObj;
    }

    const formatted = selectedDate.toISOString().split("T")[0];
    setCheckInDate(formatted);

    if (!checkOutDate || new Date(checkOutDate) <= selectedDate) {
      const nextDay = new Date(selectedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      const formattedNextDay = nextDay.toISOString().split("T")[0];
      setCheckOutDate(formattedNextDay);
    }
  };

  const handleCheckOutChange = (e) => {
    let selectedDateStr = e.target.value;
    let selectedDate = new Date(selectedDateStr); // Convert to Date

    const checkIn = new Date(checkInDate);

    if (selectedDate <= checkIn) {
      selectedDate = new Date(checkIn);
      selectedDate.setDate(checkIn.getDate() + 1);
    }

    if (selectedDate > maxDate) selectedDate = new Date(maxDate);

    const formatted = selectedDate.toISOString().split("T")[0];
    setCheckOutDate(formatted);
  };

  const getMaxGuests = useCallback(() => {
    if (type === "Double Bed") return numberOfRooms * 2;
    if (type === "Triple Bed") return numberOfRooms * 3;
    return 1;
  }, [type, numberOfRooms]);

  useEffect(() => {
    const maxGuests = getMaxGuests();
    setNumberOfGuest(Math.min(numberOfGuest, maxGuests));
    setName((prev) => prev.slice(0, maxGuests));
    setAadhar((prev) => prev.slice(0, maxGuests));
    setAge((prev) => prev.slice(0, maxGuests));
  }, [
    type,
    numberOfRooms,
    getMaxGuests,
    numberOfGuest,
    setAadhar,
    setAge,
    setName,
    setNumberOfGuest,
  ]);

  const validateFields = () => {
    const newErrors = {};

    if (!mobileNumber || !/^\d{10}$/.test(mobileNumber)) {
      newErrors.mobileNumber = "Enter a valid 10-digit mobile number.";
    } else if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Enter a valid email address.";
    } else if (!numberOfRooms || numberOfRooms === 0) {
      newErrors.numberOfRooms = "Enter the number of rooms needed.";
    } else if (!numberOfGuest || numberOfGuest === 0) {
      newErrors.numberOfGuest = "Enter the number of guests.";
    } else if (!address.district) {
      newErrors.district = "Enter the name of district.";
    } else if (!address.country) {
      newErrors.country = "Enter the name of country.";
    } else if (!address.state) {
      newErrors.state = "Enter the name of state.";
    } else if (!address.pincode || !/^\d{6}$/.test(address.pincode)) {
      newErrors.pincode = "Enter a valid 6-digit pincode.";
    } else if (!address.city) {
      newErrors.city = "Enter the name of city.";
    }

    name.forEach((n, i) => {
      if (!n) newErrors[`name${i}`] = `Enter name for person ${i + 1}.`;
    });
    aadhar.forEach((a, i) => {
      if (!/^\d{12}$/.test(a))
        newErrors[`aadhar${i}`] = `Enter a valid 12-digit Aadhar for person ${
          i + 1
        }.`;
    });
    age.forEach((a, i) => {
      if (!a || a <= 0)
        newErrors[`age${i}`] = `Enter valid age for person ${i + 1}.`;
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstInvalidKey = Object.keys(newErrors)[0];
      const invalidField = fieldRefs.current[firstInvalidKey];
      if (invalidField && invalidField.scrollIntoView) {
        invalidField.scrollIntoView({ behavior: "smooth", block: "center" });
        invalidField.focus();
      }
      return false;
    }

    return true;
  };

  const handleRoomChange = (value) => {
    const roomCount = Math.max(
      Math.min(availableRooms, parseInt(value, 10) || 0)
    );
    setNumberOfRooms(roomCount);
  };

  const handleGuestChange = (value) => {
    if (value === "" || isNaN(value) || parseInt(value, 10) < 1) {
      setNumberOfGuest(0);
      return;
    }

    let numGuest = parseInt(value, 10);
    const maxGuests = getMaxGuests();
    numGuest = Math.min(numGuest, maxGuests);

    setNumberOfGuest(numGuest);
    setName((prev) => [
      ...prev.slice(0, numGuest),
      ...new Array(Math.max(numGuest - prev.length, 0)).fill(""),
    ]);
    setAadhar((prev) => [
      ...prev.slice(0, numGuest),
      ...new Array(Math.max(numGuest - prev.length, 0)).fill(""),
    ]);
    setAge((prev) => [
      ...prev.slice(0, numGuest),
      ...new Array(Math.max(numGuest - prev.length, 0)).fill(""),
    ]);
  };

  const handleNameChange = (index, value) => {
    const updateNames = [...name];
    updateNames[index] = value;
    setName(updateNames);
  };

  const handleAgeChange = (index, value) => {
    const updateAges = [...age];
    updateAges[index] = value;
    setAge(updateAges);
  };

  const handleAadharChange = (index, value) => {
    const updateAadhar = [...aadhar];
    updateAadhar[index] = value;
    setAadhar(updateAadhar);
  };

  const handleAddressChange = (field, value) => {
    setAddress((prevAddress) => ({
      ...prevAddress,
      [field]: value,
    }));
  };

  const handleContinue = (e) => {
    e.preventDefault();
    if (validateFields()) {
      setConfirm(true);
    }
  };

  useEffect(() => {
    const checkAvailability = async () => {
      if (!checkInDate || !checkOutDate) {
        newErrors.dates = "Please select check-in and check-out dates.";
        setErrors(newErrors);
        return;
      }
      setInProgress(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_URL_SCHEME}${process.env.REACT_APP_IP_ADDRESS}/api/check-availability`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              checkInDate,
              checkOutDate,
              type,
              totalRooms,
            }),
          }
        );

        const data = await response.json();
        if (data.success && data.availableRooms > 0) {
          setAvailableRooms(data.availableRooms);
          setNumberOfRooms(data.availableRooms); // Auto set available rooms
          setInProgress(false);
          setIsAvailable(true);
        } else {
          setIsAvailable(false);
          setAvailableRooms(0);
          setInProgress(false);
        }
      } catch (error) {
        console.error("Error checking availability:", error);
      }
    };
    if (checkInDate && checkOutDate) {
      checkAvailability();
    }
    // eslint-disable-next-line
  }, [checkInDate, checkOutDate, type, totalRooms]);

  return (
    <div className="main-container">
      <h1>Book {type} Room</h1>
      <h4>
        Beds: {beds} | Price: ₹{amount} per night
      </h4>

      {errors.dates && <p className="error">{errors.dates}</p>}
      {errors.checkInDate && <p className="error">{errors.checkInDate}</p>}
      {errors.checkOutDate && <p className="error">{errors.checkOutDate}</p>}
      <div className="date">
        <label htmlFor="check-in">
          Check-In Date
          <input
            type="date"
            id="check-in"
            value={checkInDate}
            onChange={handleCheckInChange}
            min={minFormatted}
            max={maxFormatted}
            required
          />
        </label>

        <label htmlFor="check-out">
          Check-Out Date
          <input
            type="date"
            id="check-out"
            value={checkOutDate}
            onChange={handleCheckOutChange}
            min={checkInDate ? checkInDate : minFormatted}
            max={maxFormatted}
            required
          />
        </label>
      </div>
      {inProgress ? (
        <>
          <div className="spinner"></div>
          <h4>Checking availability...</h4>
        </>
      ) : (
        ""
      )}

      {dateError && <h2>{dateError}</h2>}

      {!inProgress && !isAvailable && (
        <h2>No rooms available for the selected dates.</h2>
      )}

      {availableRooms > 0 && !dateError && (
        <div className="room-booking-container">
          <form className="form">
            <label>Number of Rooms (Available: {availableRooms})</label>
            <input
              ref={(el) => (fieldRefs.current["numberOfRooms"] = el)}
              type="number"
              value={numberOfRooms}
              onChange={(e) => handleRoomChange(e.target.value)}
              min="1"
              max={availableRooms}
              required
            />
            {errors.numberOfRooms && (
              <p className="error">{errors.numberOfRooms}</p>
            )}

            <label htmlFor="guest">
              Number of Guests (Max: {getMaxGuests()})
            </label>
            <input
              ref={(el) => (fieldRefs.current["numberOfGuest"] = el)}
              type="number"
              id="guest"
              value={numberOfGuest}
              onChange={(e) => handleGuestChange(e.target.value)}
              required
            />
            {errors.numberOfGuest && (
              <p className="error">{errors.numberOfGuest}</p>
            )}

            <label htmlFor="mobile">Mobile Number</label>
            <input
              ref={(el) => (fieldRefs.current["mobileNumber"] = el)}
              type="tel"
              id="mobile"
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
              placeholder={errors.mobileNumber ? errors.mobileNumber : "Enter 10-digit mobile number"}
              required
            />
            {errors.mobileNumber && (
              <p className="error">{errors.mobileNumber}</p>
            )}

            <label htmlFor="email">Email</label>
            <input
              ref={(el) => (fieldRefs.current["email"] = el)}
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={errors.email ? errors.email : "Enter your email address"}
              required
            />
            {errors.email && <p className="error">{errors.email}</p>}

            <label>Address</label>
            <div className="address">
              <label htmlFor="country">Country</label>
              <input
                ref={(el) => (fieldRefs.current["country"] = el)}
                type="text"
                id="country"
                value={address.country}
                onChange={(e) => handleAddressChange("country", e.target.value)}
                placeholder={errors.country ? errors.country : ""}
                required
              />

              <label htmlFor="state">State</label>
              <input
                ref={(el) => (fieldRefs.current["state"] = el)}
                type="text"
                id="state"
                value={address.state}
                onChange={(e) => handleAddressChange("state", e.target.value)}
                placeholder={errors.state ? errors.state : ""}
                required
              />

              <label htmlFor="district">District</label>
              <input
                ref={(el) => (fieldRefs.current["district"] = el)}
                type="text"
                id="district"
                value={address.district}
                onChange={(e) =>handleAddressChange("district", e.target.value)}
                placeholder={errors.district ? errors.district : ""}
                required
              />

              <label htmlFor="city">City</label>
              <input
                ref={(el) => (fieldRefs.current["city"] = el)}
                type="text"
                id="city"
                value={address.city}
                onChange={(e) => handleAddressChange("city", e.target.value)}
                placeholder={errors.city ? errors.city : ""}
                required
              />

              <label htmlFor="pincode">Pincode</label>
              <input
                ref={(el) => (fieldRefs.current["pincode"] = el)}
                type="tel"
                id="pincode"
                value={address.pincode}
                onChange={(e) => handleAddressChange("pincode", e.target.value)}
                placeholder={errors.pincode ? errors.pincode : ""}
                required
              />
            </div>

            {Array.from({ length: numberOfGuest }, (_, index) => (
              <div className="form" key={`guest-${index}`}>
                <label htmlFor={`name-${index}`}>
                  Name of Person {index + 1}
                </label>
                <input
                  ref={(el) => (fieldRefs.current[`name${index}`] = el)}
                  type="text"
                  id={`name-${index}`}
                  value={name[index] || ""}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                  required
                />
                {errors[`name${index}`] && (
                  <p className="error">{errors[`name${index}`]}</p>
                )}

                <label htmlFor={`age-${index}`}>Age</label>
                <input
                  ref={(el) => (fieldRefs.current[`age${index}`] = el)}
                  type="tel"
                  id={`age-${index}`}
                  value={age[index] || ""}
                  onChange={(e) => handleAgeChange(index, e.target.value)}
                  required
                />
                {errors[`age${index}`] && (
                  <p className="error">{errors[`age${index}`]}</p>
                )}

                <label htmlFor={`aadhar-${index}`}>Aadhar Number</label>
                <input
                  ref={(el) => (fieldRefs.current[`aadhar${index}`] = el)}
                  type="number"
                  id={`aadhar-${index}`}
                  value={aadhar[index] || ""}
                  onChange={(e) => handleAadharChange(index, e.target.value)}
                  required
                />
                {errors[`aadhar${index}`] && (
                  <p className="error">{errors[`aadhar${index}`]}</p>
                )}
              </div>
            ))}
            <button
              type="submit"
              className="submit-btn"
              onClick={handleContinue}
            >
              Continue
            </button>
          </form>
        </div>
      )}
      {confirm && (
        <ConfirmBooking
          amount={amount}
          type={type}
          totalRooms={totalRooms}
          inProgress={inProgress}
          setInProgress={setInProgress}
          resetForm={() => {
            resetForm();
          }}
          close={() => {
            setConfirm(false);
          }}
        />
      )}
    </div>
  );
};

export default Roombooking;
